import type { ChartDataPoint, Pattern } from '@/entities/pattern';

import { logError } from '@/shared/lib/logging';
import { roundToDecimals } from '@/shared/lib/math';

/**
 * Selects a property from the pattern based on direction_type and parses JSON if needed
 * @param pattern - The source pattern
 * @param longProperty - Property to use if direction_type = 1
 * @param shortProperty - Property to use if direction_type = 0
 * @returns The selected property value parsed from JSON if string, or as is if not string
 */
export const getDirectionalProperty = <T>(
    pattern: Partial<Pattern>,
    longProperty: keyof Pattern,
    shortProperty: keyof Pattern,
): T => {
    const value =
        pattern.direction_type === 1
            ? pattern[longProperty]
            : pattern[shortProperty];

    if (typeof value === 'string') {
        try {
            return JSON.parse(value) as T;
        } catch (error) {
            throw new Error(
                `Failed to parse JSON for ${pattern.direction_type === 1 ? longProperty : shortProperty}: ${error}`,
            );
        }
    }

    return value as unknown as T;
};

/**
 * Gets a property from the pattern and parses JSON if needed
 * @param pattern - The source pattern
 * @param property - The property to get
 * @returns The property value parsed from JSON if string, or as is if not string
 */
export const parseArrayProperty = <T>(
    pattern: Partial<Pattern>,
    property: keyof Pattern,
): T => {
    const value = pattern[property];

    if (typeof value === 'string') {
        try {
            return JSON.parse(value) as T;
        } catch (error) {
            throw new Error(`Failed to parse JSON for ${property}: ${error}`);
        }
    }

    return value as unknown as T;
};

/**
 * Calculates the maximum value from a directional property
 * @param pattern - The source pattern
 * @param longProperty - Property to use if direction_type = 1
 * @param shortProperty - Property to use if direction_type = 0
 * @param decimals - Number of decimals to round to (default: 2)
 * @returns The maximum value, rounded to specified decimals
 */
export const getMaxDirectionalValue = (
    pattern: Partial<Pattern>,
    longProperty: keyof Pattern,
    shortProperty: keyof Pattern,
    decimals = 2,
): number => {
    const values = getDirectionalProperty<number[]>(
        pattern,
        longProperty,
        shortProperty,
    );

    return roundToDecimals(Math.max(...values), decimals);
};

/**
 * Calculates the minimum value from a directional property
 * @param pattern - The source pattern
 * @param longProperty - Property to use if direction_type = 1
 * @param shortProperty - Property to use if direction_type = 0
 * @param decimals - Number of decimals to round to (default: 2)
 * @returns The minimum value, rounded to specified decimals
 */
export const getMinDirectionalValue = (
    pattern: Partial<Pattern>,
    longProperty: keyof Pattern,
    shortProperty: keyof Pattern,
    decimals = 2,
): number => {
    const values = getDirectionalProperty<number[]>(
        pattern,
        longProperty,
        shortProperty,
    );

    return roundToDecimals(Math.min(...values), decimals);
};

/**
 * Calculates the mean value from a directional property
 * @param pattern - The source pattern
 * @param longProperty - Property to use if direction_type = 1
 * @param shortProperty - Property to use if direction_type = 0
 * @param decimals - Number of decimals to round to (default: 2)
 * @returns The mean value, rounded to specified decimals
 */
export const getMeanDirectionalValue = (
    pattern: Partial<Pattern>,
    longProperty: keyof Pattern,
    shortProperty: keyof Pattern,
    decimals = 2,
): number => {
    const values = getDirectionalProperty<number[]>(
        pattern,
        longProperty,
        shortProperty,
    );

    return roundToDecimals(
        values.reduce((acc, val) => acc + val, 0) / values.length,
        decimals,
    );
};

/**
 * Creates data points with relative time labels (t+1, t+2, etc.)
 * @param values - The numeric values
 * @returns An array of data points with relative time labels
 */
export const createDataPoints = (values: number[]): ChartDataPoint[] =>
    values.map((value, index) => ({
        value,
        timeLabel: `T + ${index + 1}`,
    }));

/**
 * Calculates the maximum value from a fixed property
 * @param pattern - The source pattern
 * @param property - Property to get the value from
 * @param decimals - Number of decimals to round to (default: 2)
 * @returns The maximum value, rounded to specified decimals
 */
export const getMaxValue = (
    pattern: Partial<Pattern>,
    property: keyof Pattern,
    decimals = 2,
): number => {
    const values = parseArrayProperty<number[]>(pattern, property);

    return roundToDecimals(Math.max(...values), decimals);
};

/**
 * Calculates the minimum value from a fixed property
 * @param pattern - The source pattern
 * @param property - Property to get the value from
 * @param decimals - Number of decimals to round to (default: 2)
 * @returns The minimum value, rounded to specified decimals
 */
export const getMinValue = (
    pattern: Partial<Pattern>,
    property: keyof Pattern,
    decimals = 2,
): number => {
    const values = parseArrayProperty<number[]>(pattern, property);

    return roundToDecimals(Math.min(...values), decimals);
};

/**
 * Calculates the mean value from a fixed property
 * @param pattern - The source pattern
 * @param property - Property to get the value from
 * @param decimals - Number of decimals to round to (default: 2)
 * @returns The mean value, rounded to specified decimals
 */
export const getMeanValue = (
    pattern: Partial<Pattern>,
    property: keyof Pattern,
    decimals = 2,
): number => {
    const values = parseArrayProperty<number[]>(pattern, property);

    return roundToDecimals(
        values.reduce((acc, val) => acc + val, 0) / values.length,
        decimals,
    );
};

/**
 * Parses correlation data from a JSON string
 * @param jsonString - The JSON string to parse
 * @returns An array of data points with name and value
 */
export const parseCorrelationData = (jsonString: string) => {
    try {
        const data = JSON.parse(jsonString);

        return data.map((item: [string, number]) => ({
            name: item[0],
            value: Math.abs(Math.round(item[1] * 100)),
        }));
    } catch (error) {
        logError('Error parsing correlation data:', error);

        return [];
    }
};
